import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import hotelService from '../services/hotelService';
import dryftService from '../services/dryftService';
import WizardStepper from './WizardStepper';
import OhipHotelCredentialStep from './OhipHotelCredentialStep';
import DryftRoutesEditor from './DryftRoutesEditor';
import DryftDryRunResult from './DryftDryRunResult';
import './AddPaymentPorterWizard.css';
import './OperaCloudCompare.css';

// Create-Dryft-project wizard: Project → Hotel & OHIP credential → Monitoring (frequency + routes)
// → Test (live dry-run). A project watches OHIP property config across hotels; each route pairs a
// Configuration Set with a Notification Target. No payment step — Dryft is a monitoring feature.

const STEPS = [
    { num: 1, label: 'Project' },
    { num: 2, label: 'Hotel' },
    { num: 3, label: 'Monitoring' },
    { num: 4, label: 'Test' },
];

const OHIP_MARKETPLACE_ID = 37;

const DryftWizard = () => {
    const navigate = useNavigate();
    const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();
    const { isSuperAdmin, isPartner } = useAuth();

    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [hotels, setHotels] = useState([]);
    const [hotelsLoading, setHotelsLoading] = useState(false);
    const [hotelData, setHotelData] = useState(null); // { hotel_id, credential } captured from step 2

    // Monitoring
    const [runFrequency, setRunFrequency] = useState('daily');
    const [routes, setRoutes] = useState([]); // [{ config_set_id, notification_target_id }]
    const [configSets, setConfigSets] = useState([]);
    const [targets, setTargets] = useState([]);

    // Test
    const [dryLoading, setDryLoading] = useState(false);
    const [dryError, setDryError] = useState(null);
    const [dryResult, setDryResult] = useState(null);

    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const loadHotels = useCallback(async () => {
        setHotelsLoading(true);
        try {
            const res = await hotelService.getAllHotels(
                selectedAccount ? { accountid: selectedAccount } : { partnerid: selectedPartner }
            );
            setHotels(res.hotels || []);
        } catch (err) {
            console.error('Failed to load hotels', err);
        } finally {
            setHotelsLoading(false);
        }
    }, [selectedAccount, selectedPartner]);

    // Config sets + notification targets — the reusable libraries the routes reference, same scope.
    const loadLibraries = useCallback(async () => {
        const filters = { account_id: selectedAccount || undefined, partner_id: selectedPartner || undefined };
        try {
            const [csRes, ntRes] = await Promise.all([
                dryftService.listConfigSets(filters),
                dryftService.listNotificationTargets(filters),
            ]);
            setConfigSets(csRes.data?.configSets || []);
            setTargets(ntRes.data?.targets || []);
        } catch (err) {
            console.error('Failed to load config sets / targets', err);
        }
    }, [selectedAccount, selectedPartner]);

    useEffect(() => { loadHotels(); loadLibraries(); }, [loadHotels, loadLibraries]);

    const effectiveAccountId = selectedAccount || null;

    const goToHotel = () => {
        if (!name.trim()) { setErrors({ name: 'Project name is required' }); return; }
        setErrors({});
        setStep(2);
    };

    // Step 2 captures the hotel + credential, then advances (project is created at the end).
    const captureHotel = ({ hotel_id, credential }) => {
        setHotelData({ hotel_id, credential });
        setStep(3);
    };

    const validRoutes = routes.filter(r => r.config_set_id && r.notification_target_id);

    const goToTest = () => {
        if (!validRoutes.length) { setErrors({ routes: 'Add at least one config set → notify route' }); return; }
        setErrors({});
        setDryResult(null);
        setDryError(null);
        setStep(4);
    };

    // Union of check-level selections across the selected routes' config sets (each row is
    // { subarea_key, check_key } — check_key null = whole sub-area).
    const collectSelections = async () => {
        const setIds = [...new Set(validRoutes.map(r => r.config_set_id))];
        const results = await Promise.all(setIds.map(id => dryftService.getConfigSet(id)));
        const seen = new Set();
        const selections = [];
        results.forEach(res => (res.data?.configSet?.subareas || []).forEach(s => {
            const k = `${s.subarea_key}::${s.check_key || ''}`;
            if (seen.has(k)) return;
            seen.add(k);
            selections.push({ subarea_key: s.subarea_key, check_key: s.check_key || null });
        }));
        return selections;
    };

    const runTest = async () => {
        setDryLoading(true);
        setDryError(null);
        setDryResult(null);
        try {
            const selections = await collectSelections();
            const res = await dryftService.dryRun({
                marketplace_id: OHIP_MARKETPLACE_ID,
                account_id:     effectiveAccountId,
                partner_id:     selectedPartner || null,
                credential:     hotelData?.credential,
                selections,
            });
            setDryResult(res.data);
        } catch (err) {
            setDryError(err.response?.data?.message || err.message || 'Live test failed');
        } finally {
            setDryLoading(false);
        }
    };

    const handleCreate = async () => {
        if (saving) return;
        setSaving(true);
        setErrors({});
        try {
            const res = await dryftService.create({
                name:           name.trim(),
                description:    description.trim() || null,
                account_id:     effectiveAccountId,
                partner_id:     selectedPartner || null,
                marketplace_id: OHIP_MARKETPLACE_ID,
                run_frequency:  runFrequency,
                first_hotel:    hotelData,
                routes:         validRoutes.map(r => ({ config_set_id: r.config_set_id, notification_target_id: r.notification_target_id })),
            });
            const newId = res.data?.id;
            navigate(newId ? `/apps/dryft/${newId}` : '/apps/dryft');
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || err.message || 'Failed to create Dryft project' });
            setSaving(false);
        }
    };

    return (
        <div className="ppw-page">
            <WizardStepper steps={STEPS} currentStep={step} />

            {step === 1 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Project details</h2>
                        <p className="ppw-card-subtitle">Name this Dryft project. It monitors OHIP property configuration across the hotels you add.</p>
                    </div>
                    <div className="ppw-card-body">
                        <div className="occ-instance-block">
                            <label className="ppw-label">Name</label>
                            <input
                                type="text"
                                className="ppw-input"
                                placeholder="e.g. Rate & package monitoring"
                                value={name}
                                onChange={e => { setName(e.target.value); setErrors(er => ({ ...er, name: undefined })); }}
                            />
                            {errors.name && <span className="ppw-error-msg">{errors.name}</span>}
                        </div>
                        <div className="occ-instance-block">
                            <label className="ppw-label">Description <span style={{ color: '#9ca3af' }}>(optional)</span></label>
                            <textarea
                                className="ppw-input"
                                rows={3}
                                placeholder="What this project watches and who should care."
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-back" onClick={() => navigate('/apps/dryft')}>← Back</button>
                        <button className="ppw-btn-next" onClick={goToHotel}>Next →</button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <OhipHotelCredentialStep
                    subtitle="Choose the hotel to monitor and the OHIP credential used to read its property configuration."
                    hotels={hotels}
                    selectedAccount={selectedAccount}
                    selectedPartner={selectedPartner}
                    isSuperAdmin={isSuperAdmin}
                    isPartner={isPartner}
                    isChainAdmin={isChainAdmin}
                    submitLabel="Next →"
                    saving={hotelsLoading}
                    onCancel={() => setStep(1)}
                    onAdd={captureHotel}
                />
            )}

            {step === 3 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Monitoring</h2>
                        <p className="ppw-card-subtitle">How often to check, and which Configuration Set alerts which Notification Target.</p>
                    </div>
                    <div className="ppw-card-body">
                        <div className="occ-instance-block">
                            <label className="ppw-label">Run frequency</label>
                            <select className="ppw-input" value={runFrequency} onChange={e => setRunFrequency(e.target.value)}>
                                <option value="daily">Daily</option>
                                <option value="hourly">Hourly</option>
                            </select>
                        </div>
                        <div className="occ-instance-block">
                            <label className="ppw-label">Routes <span style={{ color: '#9ca3af' }}>(config set → notify)</span></label>
                            <DryftRoutesEditor routes={routes} configSets={configSets} targets={targets} onChange={setRoutes} />
                            {errors.routes && <span className="ppw-error-msg">{errors.routes}</span>}
                        </div>
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-back" onClick={() => setStep(2)}>← Back</button>
                        <button className="ppw-btn-next" onClick={goToTest}>Next →</button>
                    </div>
                </div>
            )}

            {step === 4 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Test</h2>
                        <p className="ppw-card-subtitle">Optionally run a live dry-run against OHIP to confirm the credential works and preview what will be monitored.</p>
                    </div>
                    <div className="ppw-card-body">
                        {errors.submit && <div className="ppw-error-msg" style={{ marginBottom: '1rem' }}>{errors.submit}</div>}
                        <button className="occ-select-btn" onClick={runTest} disabled={dryLoading}>
                            {dryLoading ? 'Testing…' : '▶ Run live test'}
                        </button>
                        <div style={{ marginTop: 12 }}>
                            <DryftDryRunResult result={dryResult} loading={dryLoading} error={dryError} />
                        </div>
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-back" onClick={() => setStep(3)} disabled={saving}>← Back</button>
                        <button className="ppw-btn-next" onClick={handleCreate} disabled={saving}>
                            {saving ? 'Creating…' : 'Create project'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DryftWizard;
